import React, { useEffect, useState } from 'react';
import ActionButton from 'components/ActionButton';
import styles from './index.module.scss';
import DataTable from 'components/DataTable';
import { DATE_FORMATS, formatDate, getPageLimit, showError, timeToString, updatePageLimit } from 'utils';
import moment from 'moment';
import { tableStyles } from 'css/tableStyles';
import { packerSpeedReport } from 'services';
import { DateRangePickerWrapper } from 'components/Date/DateRangePicker';
import { Loading } from 'components/loading/Loading';
import CustomLink from 'components/TableLink';

let filters: {
  [p: string]: any;
  page: number;
  startDate: string | null;
  endDate: string | null;
} = {
  page: 0,
  limit: getPageLimit(),
  startDate: moment().subtract(1, 'week').toString(),
  endDate: moment().toString(),
};

const columns = [
  {
    name: 'Packer ID',
    selector: 'objectId',
    sortable: false,
    // eslint-disable-next-line react/display-name
    cell: row => (
      <CustomLink
        text={row?.objectId}
        linkTo={`/users/${row?.objectId}`}
      />
    ),
  },
  {
    name: 'Packer Name',
    selector: 'userName',
    format: row => row?.userName ?? '-',
  },
  {
    name: 'Packer Avg Time',
    selector: 'avgPackingTime',
    sortable: true,
    format: row => row?.avgPackingTime ? timeToString(row.avgPackingTime) : '-',
  },
  {
    name: 'Total Orders Packed',
    selector: 'totalOrders',
    sortable: true,
    format: row => row?.totalOrders ?? '-',
  },
];

const PackerSpeedReport = () => {
  const [data, setData] = useState<Record<string, any>[]>([]);
  const [startDate, setStartDate] = useState<string | null>(filters.startDate);
  const [endDate, setEndDate] = useState<string | null>(filters.endDate);
  const [loading, setLoading] = useState<boolean>(false);

  const fetchReport = async () => {
    setLoading(true);
    try {
      const response = await packerSpeedReport({
        ...filters,
        startDate: formatDate(filters.startDate, DATE_FORMATS.YYYY_MM_DD),
        endDate: formatDate(filters.endDate, DATE_FORMATS.YYYY_MM_DD),
      });
      setData(response);
    } catch (e) {
      showError(e);
    } finally {
      setLoading(false);
    }
  };

  const onChangePage = (page: number) => {
    filters.page = page - 1;
    fetchReport();
  };

  const onChangeRowsPerPage = (rowsPerPage: number, currentPage: number) => {
    filters.limit = rowsPerPage;
    updatePageLimit(rowsPerPage);
    onChangePage(currentPage);
  };

  const onDateSelectionChange = ({ startDate, endDate }: { startDate: Date; endDate: Date }) => {
    filters = {
      ...filters,
      startDate: moment(startDate).toISOString(),
      endDate: moment(endDate).toISOString(),
    };
    setStartDate(moment(startDate).toISOString());
    setEndDate(moment(endDate).toISOString());
  };

  const getDataTable = () => {
    if (loading) return <Loading type={'mini'} />;

    return (
      <DataTable
        keyField="objectId"
        columns={columns}
        noHeader={true}
        data={data}
        highlightOnHover={true}
        defaultSortField="avgPackingTime"
        defaultSortAsc={true}
        customStyles={tableStyles}
        pagination={true}
        paginationServer={true}
        paginationPerPage={getPageLimit()}
        paginationDefaultPage={(filters.page || 0) + 1}
        paginationTotalRows={data.length}
        paginationRowsPerPageOptions={[50, 100, 200]}
        onChangePage={onChangePage}
        onChangeRowsPerPage={onChangeRowsPerPage}
      />
    );
  };

  return (
    <>
      <div className={styles['header']}>
        <div className={styles['heading']}>Packer Speed Report</div>
        <div className={styles['filter-container']}>
          <DateRangePickerWrapper
            startDate={startDate}
            endDate={endDate}
            onDatesChange={onDateSelectionChange}
          />
          <ActionButton
            text={'Submit'}
            active={!loading}
            isLoading={loading}
            onClick={fetchReport}
            customClass={'ml-4'}
          />
        </div>
      </div>
      {getDataTable()}
    </>
  );
};

export default PackerSpeedReport;